// pages/orders/invoice/LabelPage.jsx
// Route: /orders/label/:id
// Opens in new tab → fetches order → shows shipping label → download button

import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Barcode from "react-barcode";
import orderAPI from "../../../api/orderApi";
import { usePdfDownload } from "./usePdfDownload";
import Auth from "../../../utils/AuthUtils";
import LoadingScreen from "../../../components/partials/global/LoadingScreen";
import StoreAPI from "../../../api/StoreApi";

// ── Store config ─────────────────────────────────────────────
const STORE = {
  name: Auth.getUserDetails("storeName") || "My Store",
  logo: null,
};
// ─────────────────────────────────────────────────────────────

export default function ShippingLabel() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeDetails, setStoreDetails] = useState(null);
  const labelRef = useRef(null);
  const { downloadPdf, isGenerating } = usePdfDownload();

  useEffect(() => {
    (async () => {
      try {
        const res = await orderAPI.details({ orderId: id });
        if (res.code === 200) setOrder(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (sessionStorage.getItem("storeDetails")) {
      setStoreDetails(JSON.parse(sessionStorage.getItem("storeDetails")));
    } else {
      fetchGeneralDetails();
    }
  }, []);

  const fetchGeneralDetails = async () => {
    try {
      const res = await StoreAPI.getGeneralDetails(
        Auth.getUserDetails("storeId"),
      );

      if (res.code === 200) {
        const result = res.data;
        setStoreDetails(result);
        sessionStorage.setItem("storeDetails", JSON.stringify(result));
      }
    } catch (err) {}
  };

  const handleDownload = () => {
    downloadPdf(labelRef, `label-${order?.orderId || id}.pdf`, {
      labelMode: true,
    });
  };

  if (loading) return <LoadingScreen text="Generating Label..." />;
  if (!order) return "Order not Found";

  const address = order?.address || {};
  const user = order?.user || {};
  const shipment = order?.shipment || {};

  const customerName = `${address?.firstName || user?.firstName || ""} ${
    address?.lastName || user?.lastName || ""
  }`.trim();

  const orderDate = order.orderDate
    ? new Date(order.orderDate).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  const totalItems =
    order.items?.reduce((sum, i) => sum + (i.quantity || 1), 0) || 0;

  const shipmentAwb = order?.statusHistoryAwb || {};

  return (
    <div
      className="content"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div className="mb-3">
        <button
          onClick={handleDownload}
          className="btn btn-success me-2"
          disabled={isGenerating}
          style={{
            cursor: isGenerating ? "not-allowed" : "pointer",
            opacity: isGenerating ? 0.7 : 1,
          }}
        >
          {isGenerating ? "Generating PDF..." : "Download Label"}
        </button>

        <Link to={`/orders/details/${id}`} className="btn btn-danger">
          Close
        </Link>
      </div>

      {/* ── Label ── */}
      <div
        ref={labelRef}
        style={{
          width: "378px",
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          border: "2.5px solid #000",
          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "7px 10px",
            background: "#000",
            color: "#fff",
            borderBottom: "2.5px solid #000",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {storeDetails.name}
          </span>
          <div style={{ textAlign: "right" }}>
            <div
              style={{ fontSize: "7px", opacity: 0.6, letterSpacing: "1px" }}
            >
              ORDER
            </div>
            <div
              style={{
                fontSize: "10px",
                fontWeight: "bold",
                letterSpacing: "0.5px",
              }}
            >
              #{order.orderId}
            </div>
          </div>
        </div>

        {/* Ship To */}
        <div style={{ padding: "9px 10px", borderBottom: "2.5px solid #000" }}>
          <Label>Ship To</Label>

          {order?.isShippingSameasBilling === false ? (
            // ======= SHIFT TO SHIPPING ADDRESS (When false) =======
            <>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                }}
              >
                {order?.shippingAddress?.firstName}{" "}
                {order?.shippingAddress?.lastName}
              </div>
              <div style={{ fontSize: "10px", lineHeight: 1.6, color: "#111" }}>
                {order?.shippingAddress?.addressLine1 && (
                  <div>{order?.shippingAddress?.addressLine1}</div>
                )}
                {order?.shippingAddress?.addressLine2 && (
                  <div>{order?.shippingAddress?.addressLine2}</div>
                )}
                <div>
                  {[
                    order?.shippingAddress?.district,
                    order?.shippingAddress?.state,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              </div>
              {order?.shippingAddress?.pinCode && (
                <div
                  style={{
                    display: "inline-block",
                    background: "#000",
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: "bold",
                    letterSpacing: "6px",
                    padding: "0px 5px",
                    marginTop: "6px",
                  }}
                >
                  {order?.shippingAddress?.pinCode}
                </div>
              )}
              {order?.shippingAddress?.country && (
                <div
                  style={{ fontSize: "9px", color: "#666", marginTop: "3px" }}
                >
                  {order?.shippingAddress?.country}
                </div>
              )}
              {order?.shippingAddress?.mobileNumber && (
                <div
                  style={{ fontSize: "9px", color: "#555", marginTop: "2px" }}
                >
                  Ph: {order?.shippingAddress?.mobileNumber}
                </div>
              )}
            </>
          ) : (
            // ======= SHIP TO BILLING/DEFAULT ADDRESS (When true) =======
            <>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                }}
              >
                {customerName}
              </div>
              <div style={{ fontSize: "10px", lineHeight: 1.6, color: "#111" }}>
                {address.addressLine1 && <div>{address.addressLine1}</div>}
                {address.addressLine2 && <div>{address.addressLine2}</div>}
                <div>
                  {[address.district, address.state].filter(Boolean).join(", ")}
                </div>
              </div>
              {address.pinCode && (
                <div
                  style={{
                    display: "inline-block",
                    background: "#000",
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: "bold",
                    letterSpacing: "6px",
                    padding: "0px 5px",
                    marginTop: "6px",
                  }}
                >
                  {address.pinCode}
                </div>
              )}
              {address.country && (
                <div
                  style={{ fontSize: "9px", color: "#666", marginTop: "3px" }}
                >
                  {address.country}
                </div>
              )}
              {user?.mobile && (
                <div
                  style={{ fontSize: "9px", color: "#555", marginTop: "2px" }}
                >
                  Ph: {user.mobile}
                </div>
              )}
            </>
          )}
        </div>

        {/* From */}
        <div
          style={{
            padding: "5px 10px",
            borderBottom: "1.5px solid #000",
            background: "#fafafa",
          }}
        >
          <Label>From</Label>
          <div style={{ fontSize: "10px", fontWeight: "bold" }}>
            {STORE.name}
          </div>
        </div>

        {/* Meta */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            borderBottom: "1.5px solid #000",
          }}
        >
          {[
            { label: "Order Date", value: orderDate },
            { label: "Items", value: `${totalItems} pcs` },
            {
              label: "Payment",
              value:
                order?.payment?.method === "COD" ||
                order?.payment?.method === "PARTIAL"
                  ? "COD"
                  : "PREPAID",
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                padding: "5px 8px",
                borderRight: i < 2 ? "1px solid #ccc" : "none",
              }}
            >
              <div
                style={{
                  fontSize: "7px",
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {item.label}
              </div>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  marginTop: "1px",
                }}
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {(shipment.length || shipment.weight) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              borderBottom: "1.5px solid #000",
            }}
          >
            {[
              { label: "L (cm)", value: shipment.length || "—" },
              { label: "W (cm)", value: shipment.width || "—" },
              { label: "H (cm)", value: shipment.height || "—" },
              { label: "Wt (g)", value: shipment.weight || "—" },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  padding: "4px 8px",
                  borderRight: i < 3 ? "1px solid #ccc" : "none",
                }}
              >
                <div
                  style={{
                    fontSize: "7px",
                    color: "#888",
                    textTransform: "uppercase",
                  }}
                >
                  {item.label}
                </div>
                <div style={{ fontSize: "10px", fontWeight: "bold" }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Barcode */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            borderBottom: "1.5px solid #000",
            background: "#fff",
          }}
        >
          <Barcode
            value={shipmentAwb?.awb || order?.orderId || "ORDER"}
            format="CODE128"
            width={1.5}
            height={45}
            fontSize={10}
            margin={4}
          />
        </div>

        {/* Items List */}
        <div style={{ padding: "6px 10px", flex: 1 }}>
          <Label>Items</Label>
          {order.items?.slice(0, 6).map((item, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "9px",
                padding: "2px 0",
                borderBottom: "0.5px solid #eee",
                color: "#222",
              }}
            >
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  paddingRight: "8px",
                }}
              >
                {item.productName}
                {item.variantName ? ` — ${item.variantName}` : ""}
              </span>
              <span style={{ fontWeight: "bold" }}>x{item.quantity}</span>
            </div>
          ))}
          {order.items?.length > 6 && (
            <div style={{ fontSize: "8px", color: "#888", marginTop: "3px" }}>
              +{order.items.length - 6} more item(s)
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "4px 10px",
            background: "#f0f0f0",
            borderTop: "1px solid #ccc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "7px",
            color: "#383838",
          }}
        >
          <div>
            <span>
              Collect Amount: ₹
              {Number(
                order?.payment?.method === "PARTIAL"
                  ? order?.payment?.partialPayment?.balanceAmount
                  : order?.payment?.method === "COD"
                    ? order?.total
                    : 0,
              ).toLocaleString("en-IN")}
            </span>
          </div>

          <span>
            Printed:{" "}
            {new Date().toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>
      </div>

      <div style={{ height: "24px" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Label({ children }) {
  return (
    <div
      style={{
        fontSize: "7px",
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: "1px",
        color: "#888",
        marginBottom: "3px",
      }}
    >
      {children}
    </div>
  );
}
